import { ChevronLeft, MessageCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const sections = [
  {
    title: "Getting Started",
    faqs: [
      {
        q: "How do I set up my athlete profile?",
        a: "When you first sign in, LegacyLoop walks you through a setup flow to create your athlete's profile. You'll enter their name, sport, position, graduation year, GPA, school, and a short bio. You can update any of this later from the Dashboard.",
      },
      {
        q: "Can I add more than one athlete?",
        a: "Yes. The Free plan supports 1 athlete profile. Loop Family and Loop Recruit both support 2 athlete profiles. Use the athlete switcher in the header to add and switch between them.",
      },
      {
        q: "How do I invite family members?",
        a: "Go to The Loop and enter their email address. You can choose their access level: View (see media and journal), Comment, or Admin (full access). They'll receive an invite email with instructions to join.",
      },
      {
        q: "Is LegacyLoop available as a mobile app?",
        a: "Not yet. LegacyLoop is a web app that works on any browser, including mobile browsers on iOS and Android. A dedicated mobile app is on our roadmap.",
      },
    ],
  },
  {
    title: "Media & Vault",
    faqs: [
      {
        q: "What file types can I upload?",
        a: "LegacyLoop supports photos (JPEG, PNG, HEIC/HEIF from iPhone), videos (MP4, MOV), and audio files. HEIC files are automatically converted for browser compatibility.",
      },
      {
        q: "What is the Vault?",
        a: "The Vault is a private section of your Media Library that only you can see. To move a media item to the Vault, open it in the Media Library and toggle the Vault option. People in The Loop with view access cannot see Vault content.",
      },
      {
        q: "What are the storage limits for each plan?",
        a: "Free: 500 MB. Loop Family: 1 TB. Loop Recruit: 2 TB. Free plan uploads are also capped at 50 MB per file. Upgrade from the Subscription page to unlock more storage.",
      },
      {
        q: "Can I download my photos and videos?",
        a: "Yes. In the Media Library, open any item and select Download from the options menu. Downloads go directly to your device.",
      },
      {
        q: "What is Recent Highlights on the Dashboard?",
        a: "Recent Highlights shows your four most recently uploaded media items directly on the Dashboard so your best moments are always front and center. Click 'View all' to go to the full Media Library.",
      },
    ],
  },
  {
    title: "Recruiting Center",
    faqs: [
      {
        q: "What is the Contact Window?",
        a: "The Contact Window shows the NCAA and NAIA recruiting calendar for your athlete's sport and division, including when coaches are legally allowed to initiate contact. Dates are updated to the current 2025-26 academic year.",
      },
      {
        q: "How does the AI Email Writer work?",
        a: "Select the type of email you want to write (introduction, follow-up, visit request, or commitment), enter the school and coach details, and our AI generates a personalized draft. You can edit it before sending. The email is never sent automatically.",
      },
      {
        q: "What is the College Interest Tracker?",
        a: "The College Interest Tracker lets you track schools your athlete is interested in, rate interest level from 1 to 5, and add notes. It includes 400+ Division I and II schools with official logos. Your tracker syncs across devices.",
      },
      {
        q: "Can coaches see my athlete's profile on LegacyLoop?",
        a: "No. LegacyLoop is a private family platform. Coaches cannot search for or view your athlete's profile unless you explicitly share a Loop Link with them.",
      },
    ],
  },
  {
    title: "The Loop",
    faqs: [
      {
        q: "Who can I invite to The Loop?",
        a: "Anyone with an email address: grandparents, aunts and uncles, coaches you trust, or close family friends. You control their access level and can remove them at any time.",
      },
      {
        q: "What can family members see based on their access level?",
        a: "View: Can see media and journal entries. Comment: Can view and leave comments. Admin: Full access including uploading and editing. Vault content is always private and never visible to family members regardless of access level.",
      },
      {
        q: "What is a Loop Link?",
        a: "A Loop Link is a shareable read-only link to a curated set of highlights. It does not require the recipient to create an account. You control what's included and can disable the link at any time.",
      },
      {
        q: "How do I remove a family member?",
        a: "Go to The Loop, find the member you want to remove, and click the remove button next to their name. They will immediately lose access to your athlete's content.",
      },
    ],
  },
  {
    title: "Billing & Plans",
    faqs: [
      {
        q: "What is included in the Free plan?",
        a: "The Free plan includes 1 athlete profile, 500 MB of storage, and access to the core features: media uploads, journal, The Loop, and recruiting tools. Media uploaded on the Free plan includes a watermark.",
      },
      {
        q: "How do I upgrade my plan?",
        a: "Go to the Subscription page from your Dashboard. Choose your plan and complete checkout with a credit or debit card. Your new plan activates immediately.",
      },
      {
        q: "What happens to my data if I cancel?",
        a: "Your data is retained for 30 days after cancellation, giving you time to download your media. After 30 days, your account and uploaded content are permanently deleted. We recommend downloading your media before canceling.",
      },
      {
        q: "Do you offer refunds?",
        a: "Subscription charges are non-refundable except where required by law. If you believe you were charged in error, contact us at support@mylegacyloop.com and we'll review your case.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) and Apple Pay where supported. All payments are securely processed by Stripe.",
      },
    ],
  },
];

export default function HelpCenter() {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">

        <a href="/" className="inline-flex items-center text-[#E0A458] hover:text-[#f0b467] text-sm mb-8 transition-colors">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Home
        </a>

        <div className="rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-6 py-6 shadow-lg shadow-black/25 mb-8">
          <h1 className="font-montserrat font-bold text-3xl text-white">Help Center</h1>
          <p className="text-slate-400 text-sm mt-1">Answers to the most common questions about LegacyLoop.</p>
        </div>

        <div className="space-y-8">
          {sections.map((section) => (
            <div key={section.title}>
              <h2 className="font-montserrat font-bold text-xl text-white border-l-2 border-[#E0A458] pl-3 mb-4">
                {section.title}
              </h2>
              <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 overflow-hidden">
                <Accordion type="multiple">
                  {section.faqs.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`${section.title}-${i}`}
                      className="border-b border-[#1B263B] last:border-b-0"
                    >
                      <AccordionTrigger className="px-5 py-4 text-sm font-medium text-white hover:text-[#E0A458] hover:no-underline text-left">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-4 text-sm text-slate-300 leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-6 py-6 text-center">
          <MessageCircle className="h-8 w-8 text-[#E0A458]/60 mx-auto mb-3" />
          <p className="text-white font-medium mb-1">Still have questions?</p>
          <p className="text-slate-400 text-sm mb-4">Our team typically responds within 1-2 business days.</p>
          <a
            href="/contact"
            className="inline-flex items-center px-6 py-2 bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold rounded-md text-sm transition-colors"
          >
            Contact Us
          </a>
        </div>

      </div>
    </div>
  );
}
